// AdminShell — the desktop chrome wrapping every admin page.
//
// Per shell-strings.md §"Shell composition":
//   232px dark Sidebar + main column (Topbar + content slot)
//
// Shell-level states (§"Shell-level states"):
//   - normal:        sidebar + topbar + children render
//   - loading:       sidebar + topbar persist; content shows skeleton + spinner
//   - error-401:     sidebar + topbar persist; content swaps to session-expired panel
//   - error-network: sidebar + topbar persist; content swaps to network-lost panel
//
// The chrome NEVER blanks during loading/error — only the content slot changes.

import type { ReactNode } from 'react';
import { Spinner } from '@gc-coach/design-system';
import { useAuth } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { env } from '../config/env.js';
import { Sidebar } from './Sidebar.js';
import { Topbar, type TopbarProps } from './Topbar.js';
import type { EnvLabel } from './EnvBadge.js';
import type { NavItemKey } from './navModel.js';
import styles from './AdminShell.module.css';

export type ShellState =
  | { kind: 'normal' }
  | { kind: 'loading' }
  | { kind: 'error-401'; onReauth: () => void }
  | { kind: 'error-network'; onRetry: () => void };

export interface AdminShellProps {
  /** Currently-active sidebar item — drives visual active state. */
  activeKey?: NavItemKey;
  /** Breadcrumb segments shown in the Topbar. */
  breadcrumb: ReadonlyArray<string>;
  /** Optional unread notifications count for the Topbar bell. */
  notificationCount?: number;
  /** Click handler for the Topbar bell — opens the notifications panel. */
  onOpenNotifications?: TopbarProps['onOpenNotifications'];
  /** Override the auto-detected env (from VITE_ENV_LABEL). Useful for tests. */
  envOverride?: EnvLabel;
  /** What to render in the content slot — normally page content. */
  children: ReactNode;
  /** Optional shell-level state overlay; defaults to `'normal'`. */
  state?: ShellState;
}

export function AdminShell({
  activeKey,
  breadcrumb,
  notificationCount,
  onOpenNotifications,
  envOverride,
  children,
  state = { kind: 'normal' },
}: AdminShellProps) {
  const envLabel = envOverride ?? resolveEnvLabel(env.envLabel);

  return (
    <div className={styles.shell}>
      <Sidebar {...(activeKey !== undefined ? { activeKey } : {})} env={envLabel} />
      <div className={styles.main}>
        <Topbar
          breadcrumb={breadcrumb}
          {...(notificationCount !== undefined ? { notificationCount } : {})}
          {...(onOpenNotifications !== undefined ? { onOpenNotifications } : {})}
        />
        <main className={styles.content}>
          <ShellStateView state={state}>{children}</ShellStateView>
        </main>
      </div>
    </div>
  );
}

// ─── State view ──────────────────────────────────────────────────────────────

function ShellStateView({ state, children }: { state: ShellState; children: ReactNode }) {
  const { t } = useI18n();

  switch (state.kind) {
    case 'normal':
      return <>{children}</>;

    case 'loading':
      return (
        <div className={styles.statePanel} role="status" aria-live="polite">
          <Spinner />
          <span className={styles.stateCaption}>{t('shell.stateLoading')}</span>
        </div>
      );

    case 'error-401':
      return (
        <StatePanel
          title={t('shell.state401Title')}
          body={t('shell.state401Body')}
          button={t('shell.state401Button')}
          onClick={state.onReauth}
        />
      );

    case 'error-network':
      return (
        <StatePanel
          title={t('shell.stateNetworkTitle')}
          body={t('shell.stateNetworkBody')}
          button={t('shell.stateNetworkButton')}
          onClick={state.onRetry}
        />
      );
  }
}

function StatePanel({
  title,
  body,
  button,
  onClick,
}: {
  title: string;
  body: string;
  button: string;
  onClick: () => void;
}) {
  return (
    <div className={styles.statePanel} role="alert">
      <h2 className={styles.stateTitle}>{title}</h2>
      <p className={styles.stateBody}>{body}</p>
      <button type="button" className={styles.stateButton} onClick={onClick}>
        {button}
      </button>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Map the raw VITE_ENV_LABEL onto the closed EnvLabel union. Unknown → 'dev'. */
function resolveEnvLabel(raw: string): EnvLabel {
  if (raw === 'prod' || raw === 'staging' || raw === 'dev') return raw;
  return 'dev';
}

// Re-export for callers (App.tsx etc.) so they don't have to import directly.
export { useAuth };
